'use client'
/* eslint-disable */
// @ts-nocheck
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { cn } from '@/utils'
import { useToolbar } from './toolbar-provider'
import type { Extension } from '@tiptap/core'
import type { ColorOptions } from '@tiptap/extension-color'
import type { HighlightOptions } from '@tiptap/extension-highlight'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMediaQuery } from '@/hooks/use-media-query'
import { MobileToolbarGroup, MobileToolbarItem } from './mobile-toolbar-group'

type TextStylingExtensions =
  | Extension<ColorOptions, any>
  | Extension<HighlightOptions, any>

const TEXT_COLORS = [
  { nameKey: 'default', color: 'var(--foreground)' },
  { nameKey: 'muted', color: 'var(--muted-foreground)' },
  { nameKey: 'primary', color: 'var(--primary)' },
  { nameKey: 'secondary', color: 'var(--secondary-foreground)' },
  { nameKey: 'success', color: 'var(--success-foreground)' },
  { nameKey: 'warning', color: 'var(--warning-foreground)' },
  { nameKey: 'danger', color: 'var(--destructive-foreground)' },
  { nameKey: 'info', color: 'var(--info-foreground)' }
]

const HIGHLIGHT_COLORS = [
  { nameKey: 'default', color: 'var(--muted)' },
  { nameKey: 'accent', color: 'var(--accent)' },
  { nameKey: 'secondary', color: 'var(--secondary)' },
  { nameKey: 'success', color: 'var(--success)' },
  { nameKey: 'warning', color: 'var(--warning)' },
  { nameKey: 'danger', color: 'var(--destructive)' },
  { nameKey: 'info', color: 'var(--info-accent)' }
]

interface ColorHighlightButtonProps {
  name: string
  color: string
  isActive: boolean
  onClick: () => void
  isHighlight?: boolean
}

const ColorHighlightButton = ({
  name,
  color,
  isActive,
  onClick,
  isHighlight
}: ColorHighlightButtonProps) => (
  <button
    onClick={onClick}
    className="hover:bg-gray-3 flex w-full items-center justify-between rounded-sm px-2 py-1 text-sm"
    type="button"
  >
    <div className="flex items-center space-x-2">
      <div
        className="rounded-sm border px-1 py-px font-medium"
        style={isHighlight ? { backgroundColor: color } : { color }}
      >
        A
      </div>
      <span>{name}</span>
    </div>
    {isActive && <CheckIcon className="h-4 w-4" />}
  </button>
)

export const ColorHighlightToolbar = () => {
  const { editor } = useToolbar()
  const t = useTranslations('components.tiptap.toolbars.colorAndHighlight')
  const isMobile = useMediaQuery('(max-width: 640px)')

  const currentColor = editor?.getAttributes('textStyle').color
  const currentHighlight = editor?.getAttributes('highlight').color

  const handleSetColor = (color: string) => {
    editor
      ?.chain()
      .focus()
      .setColor(color === currentColor ? '' : color)
      .run()
  }

  const handleSetHighlight = (color: string) => {
    editor
      ?.chain()
      .focus()
      .setHighlight(color === currentHighlight ? { color: '' } : { color })
      .run()
  }

  const isDisabled =
    !editor?.can().chain().setHighlight().run() ||
    !editor?.can().chain().setColor('').run()

  if (isMobile) {
    return (
      <div className="flex gap-1">
        <MobileToolbarGroup label={t('color')}>
          {TEXT_COLORS.map(({ nameKey, color }) => (
            <MobileToolbarItem
              key={nameKey}
              onClick={() => handleSetColor(color)}
              active={currentColor === color}
            >
              <div className="flex items-center gap-2">
                <div className="rounded-sm border px-2" style={{ color }}>
                  A
                </div>
                <span>{t(`colors.${nameKey}` as never)}</span>
              </div>
            </MobileToolbarItem>
          ))}
        </MobileToolbarGroup>

        <MobileToolbarGroup label={t('highlight')}>
          {HIGHLIGHT_COLORS.map(({ nameKey, color }) => (
            <MobileToolbarItem
              key={nameKey}
              onClick={() => handleSetHighlight(color)}
              active={currentHighlight === color}
            >
              <div className="flex items-center gap-2">
                <div
                  className="rounded-sm border px-2"
                  style={{ backgroundColor: color }}
                >
                  A
                </div>
                <span>{t(`colors.${nameKey}` as never)}</span>
              </div>
            </MobileToolbarItem>
          ))}
        </MobileToolbarGroup>
      </div>
    )
  }

  return (
    <Popover>
      <div className="relative h-full">
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger disabled={isDisabled} asChild>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                style={{
                  color: currentColor
                }}
                className={cn('h-8 w-14 p-0 font-normal')}
              >
                <span className="text-md">A</span>
                <ChevronDownIcon className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('title')}</TooltipContent>
        </Tooltip>

        <PopoverContent align="start" className="dark:bg-gray-2 w-56 p-1">
          <ScrollArea className="max-h-80 overflow-y-auto pr-2">
            <div className="text-gray-11 mt-2 mb-2.5 px-2 text-xs">
              {t('color')}
            </div>
            {TEXT_COLORS.map(({ nameKey, color }) => (
              <ColorHighlightButton
                key={nameKey}
                name={t(`colors.${nameKey}` as never)}
                color={color}
                isActive={currentColor === color}
                onClick={() => handleSetColor(color)}
              />
            ))}

            <Separator className="my-3" />

            <div className="text-gray-11 mb-2.5 w-full px-2 pr-3 text-xs">
              {t('background')}
            </div>
            {HIGHLIGHT_COLORS.map(({ nameKey, color }) => (
              <ColorHighlightButton
                key={nameKey}
                name={t(`colors.${nameKey}` as never)}
                color={color}
                isActive={currentHighlight === color}
                onClick={() => handleSetHighlight(color)}
                isHighlight
              />
            ))}
          </ScrollArea>
        </PopoverContent>
      </div>
    </Popover>
  )
}
